<template>
    <div class="topbar clearfix">
        <div class="topbar-left">
            <router-link to="/" class="topbar-logo-link">
                <div class="logo-wrapper">
                    <button class="p-link">
                        <img alt="ACADEMICO" :src="`${baseUrl}/images/logo-academico.png`" class="topbar-logo"/>
                    </button>
                    <span class="brand-text">Plataforma LEC III</span>
                </div>
            </router-link> 
        </div>

        <div class="topbar-right">

            <button class="p-link" id="menu-button" @click="onMenuButtonClick">
                <i class="pi pi-angle-left"></i>
            </button>

            <button class="p-link" id="topbar-menu-button" @click="onTopbarMenuButtonClick">
                <i class="pi pi-bars"></i>
            </button>


            <ul :class="topbarItemsClass">
                
                <li v-if="profileMode === 'top' || horizontal"
                    :class="['profile-item', {'active-top-menu': activeTopbarItem === 'profile'}]"
                    @click="$emit('topbar-item-click',{originalEvent:$event,item:'profile'})">
                    <button class="p-link">
                        <img alt="User" class="profile-image" :src="`${baseUrl}/images/user-m.png`"/>
                        <div>
                            <span class="topbar-item-name">{{ getUser()?.name }}</span>
                            <span class="topbar-item-role">{{ getUser()?.email }}</span>
                        </div>
                    </button>

                    <transition name="layout-submenu-container">
                        <ul class="layout-menu fadeInDown" v-show="activeTopbarItem === 'profile'">    
                            <li role="menuitem">
                                <button class="p-link topbar-item-button" @click="logout">
                                    <i class="pi pi-fw pi-sign-out"></i>
                                    <span>Salir</span>
                                </button>
                            </li>
                        </ul>
                    </transition>
                </li>

                <li>
                    <button class="p-link" @click="toggleTheme">
                        <i :class="['topbar-icon pi', layoutScheme === 'dark' ? 'pi-sun' : 'pi-moon']"></i>
                        <span class="topbar-item-name">{{ layoutScheme === 'dark' ? 'Tema Claro' : 'Tema Oscuro' }}</span>
                    </button>
                </li>
                <li v-if="authStore.isAuthenticated" class="session-timer-item">
                    <div class="timer-container" :class="{'timer-danger': isLowTime}">
                        <i class="pi pi-clock"></i>
                        <div class="timer-text">
                            <span class="timer-label">Sesión Expira en: </span>
                            <span class="timer-value">{{ displayTime }}</span>
                        </div>
                        <button class="p-link refresh-button" @click="renewSession" title="Renovar sesión">
                            <i class="pi pi-sync"></i>
                        </button>
                    </div>
                </li>

                
            </ul>
        </div>
    </div>
</template>

<script>
    import { useAuthStore } from '@/stores/authStore'; 
    import { AuthService } from '@/service/AuthService';
    import { ref, onMounted, onUnmounted } from 'vue';

    export default {
        props: {
            topbarMenuActive: Boolean,
            profileMode: String,
            horizontal: Boolean,
            activeTopbarItem: String,
            layoutScheme: String
        },
        setup() {
            const authStore = useAuthStore();
            const displayTime = ref('00:00:00');
            const isLowTime = ref(false);
            let timer = null;

            const updateCountdown = () => {
                const expiration = authStore.expiration;
                if (!expiration) return;

                const now = Date.now();
                const diff = expiration - now;

                if (diff <= 0) {
                    displayTime.value = '00:00:00';
                    authStore.logout();
                    return;
                }

                // --- CÁLCULO DE HORAS, MINUTOS Y SEGUNDOS ---
                const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((diff / 1000 / 60) % 60);
                const seconds = Math.floor((diff / 1000) % 60);
                
                // Alertar si queda menos de 3 minutos
                isLowTime.value = hours === 0 && minutes < 5;
                
                displayTime.value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
            };

            onMounted(() => {
                timer = setInterval(updateCountdown, 1000);
            });

            onUnmounted(() => {
                if (timer) clearInterval(timer);
            });

            return {
                authStore,
                displayTime,
                isLowTime
            }
        },
        data() {
            return {
                baseUrl: process.env.VUE_APP_BASE_URL || '',
            };
        },
        methods: {
            // AGREGAMOS ESTA FUNCIÓN PARA SOLUCIONAR EL ERROR
            getUser() {
                return this.authStore.user;
            },
            async renewSession() {
                try {
                    const response = await AuthService.refresh(); // Asegúrate de tener este método en AuthService
                    const { access_token, user, expires_in } = response.data;
                    this.authStore.login(access_token, user, expires_in);
                    this.$toast.add({ severity: 'success', summary: 'Éxito', detail: 'Sesión renovada', life: 2000 });
                } catch (error) {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo renovar la sesión', life: 3000 });
                }
            },
            onMenuButtonClick(event) {
                this.$emit('menubutton-click', event);
            },
            onTopbarMenuButtonClick(event) {
                this.$emit('topbar-menubutton-click', event);
            },
            toggleTheme() {
                const newScheme = this.layoutScheme === 'dark' ? 'light' : 'dark';
                this.$emit('layout-scheme-change', newScheme);
            },
            async logout() {
                try {
                    await AuthService.logout();
                } catch (error) {
                    console.warn("No se pudo cerrar sesión en el servidor:", error.message);
                } finally {
                    this.authStore.logout();
                    this.$router.push('/login');
                    this.$toast.add({
                        severity: 'info', 
                        summary: 'Sesión Finalizada', 
                        detail: 'Has salido del sistema', 
                        life: 3000
                    });
                }
            }
        },
        computed: {
            topbarItemsClass() {
                return ['topbar-items fadeInDown', {
                    'topbar-items-visible': this.topbarMenuActive
                }];
            }
        }
    }
</script>
<style scoped>
.topbar-logo-link {
    text-decoration: none; /* Evita subrayados azules de links */
}

.logo-wrapper {
    display: flex;
    align-items: center;    /* Centrado Vertical */
    justify-content: center; /* Centrado Horizontal */
    gap: 12px;              /* Espacio entre logo y nombre */
}

.topbar-logo {
    height: 35px;           /* Ajusta según el alto de tu barra */
    width: auto;
    display: block;
}

.brand-text {
    color: #ffffff;         /* Blanco puro para máximo contraste */
    font-size: 1.4rem;      /* Tamaño destacado */
    /*font-weight: 800;        Extra negrita para que se vea robusto */
    text-transform: uppercase;
    letter-spacing: 1.5px;  /* Estilo moderno tipo marca */
    
    /* Suavizado de fuente para fondos oscuros/de color */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    /* Alineación de línea de base para evitar saltos */
    line-height: 1;
}

.logo-button {
    padding: 0;
    line-height: 0;         /* Elimina espacios extra del botón */
}


/* ESTILOS PARA EL CONTADOR */
.session-timer-item {
    display: flex;
    align-items: center;
    padding: 0 1rem;
    margin-top: -6px;
}

.timer-container {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.1);
    padding: 5px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    transition: all 0.3s;
}

.timer-danger {
    background: rgba(255, 61, 61, 0.2);
    border-color: #ff3d3d;
    animation: pulse 2s infinite;
}

.timer-text {
    display: flex;
    flex-direction: column;
    margin: 0 10px;
    line-height: 1;
}

.timer-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    /* opacity: 0.8; */
}

.timer-value {
    /* font-family: 'Courier New', Courier, monospace; */
    font-weight: bold;
    font-size: 1.1rem;
}

.refresh-button {
    color: white;
    padding: 4px;
    border-radius: 50%;
    transition: transform 0.2s;
}

.refresh-button:hover {
    transform: rotate(180deg);
    background: rgba(255, 255, 255, 0.2);
}

@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 61, 61, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(255, 61, 61, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 61, 61, 0); }
}
</style>